/**
 * app/components/CustomAlert.tsx
 * -------------------------------
 * What: Reusable alert/modal component for confirmations and simple prompts.
 * Why: Provides consistent styling and behaviour across the app.
 */
// CustomAlert.tsx
//
// This file defines the CustomAlert component, a reusable modal alert for the app.
// It provides a themed, accessible alert dialog with a title, message, and OK button.
//
// Usage:
//   - Used anywhere in the app where you want to show a custom alert/modal dialog.
//   - Called from screens or components to display important messages or errors.
//   - More customizable and accessible than the default Alert API.
//
// Features:
//   - Themed colors (from ThemeContext)
//   - Accessible: announces to screen readers, proper roles/labels
//   - Dismisses on overlay press or OK button
//
// Example:
//   <CustomAlert visible={showAlert} title="Error" message="Something went wrong" onDismiss={() => setShowAlert(false)} />

import React, { useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, AccessibilityInfo, Pressable } from 'react-native';
import { useTheme } from '../../lib/ThemeContext';

// Props for the CustomAlert component
// - visible: whether the alert is shown
// - title: alert title text
// - message: alert message text
// - onDismiss: function to call when alert is dismissed
//
type CustomAlertProps = {
  visible: boolean;
  title: string;
  message: string;
  onDismiss: () => void;
};

// CustomAlert component
export default function CustomAlert({ visible, title, message, onDismiss }: CustomAlertProps) {
  const { theme } = useTheme(); // Get current theme
  const alertRef = useRef<View>(null);

  useEffect(() => {
    if (visible) {
      // Announce the alert to screen readers for accessibility
      AccessibilityInfo.announceForAccessibility(`${title}. ${message}`);
    }
  }, [visible, title, message]);

  return (
    // Modal provides the overlay and handles showing/hiding
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
      statusBarTranslucent
      accessibilityViewIsModal
    >
      {/* Overlay: darkens background, dismisses alert on press */}
      <Pressable 
        style={[styles.overlay, { backgroundColor: theme.modalOverlay }]}
        onPress={onDismiss}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel="Dismiss alert"
        accessibilityHint="Double tap to dismiss the alert"
      >
        {/* Alert container: prevents overlay press from bubbling, contains alert content */}
        <Pressable 
          ref={alertRef}
          style={[styles.alertContainer, { backgroundColor: theme.card }]}
          onPress={e => e.stopPropagation()}
          accessible={true}
          accessibilityRole="alert"
          accessibilityLabel={`${title}. ${message}`}
          accessibilityHint="Press OK to dismiss"
        >
          {/* Title text */}
          <Text style={[styles.title, { color: theme.text }]}>{title}</Text>
          {/* Message text */}
          <Text style={[styles.message, { color: theme.text }]}>{message}</Text>
          {/* OK button to dismiss alert */}
          <TouchableOpacity
            style={[styles.button, { backgroundColor: theme.buttonBackground }]}
            onPress={onDismiss}
            accessibilityRole="button"
            accessibilityLabel="OK"
            accessibilityHint="Dismiss alert"
          >
            <Text style={[styles.buttonText, { color: theme.buttonText }]}>OK</Text>
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

// Styles for overlay, alert container, text, and button
const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  alertContainer: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 20,
    width: '80%',
    maxWidth: 400,
    elevation: 5,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  message: {
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
  },
  button: {
    backgroundColor: '#007AFF',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
}); 